import { useState } from 'react'
import { Button, ComboBox, Input, ListBox, ListBoxItem, Popover } from 'react-aria-components'
import { Icon } from '@/shared/components/atoms/Icon'
import { cn } from '@/lib/utils'

interface StaticOption {
	label: string
	value: string
}

interface Props {
	id?: string
	ariaLabel?: string
	ariaInvalid?: boolean
	ariaDescribedBy?: string
	disabled?: boolean
	placeholder?: string
	options: StaticOption[]
	value: string[]
	onChange: (next: string[]) => void
	onBlur?: () => void
}

/**
 * Multi-select ComboBox over a fixed option list. Matches the visual pattern
 * of the data-source-backed `AutocompleteField` (multi mode) — a chip row for
 * the picked values plus a ComboBox input for adding more. Used for static
 * `multiselect` fields so the form UI feels consistent.
 *
 * Design notes:
 *  - Each pick is appended to the value array; already-picked options can be
 *    picked again only if the caller wants duplicates (we filter them out
 *    here for the simpler single-pick-per-option semantic).
 *  - The chip row is above the input; removing a chip calls `onChange` with
 *    the filtered array.
 */
export function StaticMultiComboBoxField(props: Props) {
	const { value, onChange, options, disabled } = props
	const [inputValue, setInputValue] = useState('')
	const labelByValue = new Map(options.map((o) => [o.value, o.label]))
	const availableOptions = options.filter((o) => !value.includes(o.value)).map((o) => ({ id: o.value, ...o }))

	const handleSelectionChange = (val: string | number | null) => {
		if (val === null) return
		const stringVal = String(val)
		if (!value.includes(stringVal)) onChange([...value, stringVal])
		setInputValue('')
	}
	const removeChip = (v: string) => onChange(value.filter((x) => x !== v))

	return (
		<div className="space-y-2">
			{value.length > 0 ? (
				<ChipRow values={value} labelByValue={labelByValue} disabled={disabled} onRemove={removeChip} />
			) : null}
			<ComboBoxInput
				props={props}
				items={availableOptions}
				inputValue={inputValue}
				setInputValue={setInputValue}
				onSelectionChange={handleSelectionChange}
			/>
		</div>
	)
}

function ChipRow({
	values,
	labelByValue,
	disabled,
	onRemove,
}: {
	values: string[]
	labelByValue: Map<string, string>
	disabled?: boolean
	onRemove: (v: string) => void
}) {
	return (
		<div className="flex flex-wrap gap-1">
			{values.map((v) => (
				<span
					key={v}
					className="inline-flex items-center gap-1 rounded-md border border-input bg-muted px-2 py-0.5 text-xs"
				>
					{labelByValue.get(v) ?? v}
					<button
						type="button"
						aria-label={`Remove ${labelByValue.get(v) ?? v}`}
						className="text-muted-foreground hover:text-foreground"
						onClick={() => onRemove(v)}
						disabled={disabled}
					>
						<Icon name="X" size={12} />
					</button>
				</span>
			))}
		</div>
	)
}

function ComboBoxInput({
	props,
	items,
	inputValue,
	setInputValue,
	onSelectionChange,
}: {
	props: Props
	items: Array<StaticOption & { id: string }>
	inputValue: string
	setInputValue: (next: string) => void
	onSelectionChange: (val: string | number | null) => void
}) {
	return (
		<ComboBox
			id={props.id}
			aria-label={props.ariaLabel}
			aria-invalid={props.ariaInvalid}
			aria-describedby={props.ariaDescribedBy}
			defaultItems={items}
			inputValue={inputValue}
			onInputChange={setInputValue}
			onSelectionChange={onSelectionChange}
			selectedKey={null}
			isDisabled={props.disabled}
			allowsEmptyCollection
			menuTrigger="focus"
			className="relative flex flex-col gap-1"
			onBlur={props.onBlur}
		>
			<Input placeholder={props.placeholder ?? 'Pick an option…'} className={cn(INPUT_CLASS, 'pr-9')} />
			<Button
				aria-label="Open options"
				className="absolute right-0 bottom-0 flex h-10 items-center px-2 text-muted-foreground"
			>
				<Icon name="ChevronDown" size="sm" aria-hidden />
			</Button>
			<OptionsPopover />
		</ComboBox>
	)
}

function OptionsPopover() {
	return (
		<Popover
			placement="bottom start"
			offset={4}
			shouldFlip={false}
			className="z-50 max-h-60 w-[var(--trigger-width)] overflow-auto rounded-md border border-input bg-popover text-popover-foreground shadow-md"
		>
			<ListBox className="p-1 outline-none">
				{(item: StaticOption & { id: string }) => (
					<ListBoxItem
						textValue={item.label}
						className="cursor-pointer rounded-sm px-2 py-1.5 text-sm outline-none data-[focused]:bg-accent data-[focused]:text-accent-foreground hover:bg-accent hover:text-accent-foreground"
					>
						{item.label}
					</ListBoxItem>
				)}
			</ListBox>
		</Popover>
	)
}

const INPUT_CLASS = cn(
	'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background',
	'placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
	'disabled:cursor-not-allowed disabled:opacity-50 md:text-sm',
)
